import React, { useState, useRef } from 'react';
import { BACKEND_URL } from '../config';

import {
  ArrowsClockwise,
  ChartBar,
  Check,
  FileImage,
  Lock,
  Pencil,
  Plus,
  Sparkle,
  Trash,
  UploadSimple,
  X
} from '@phosphor-icons/react';
const UploadsView = ({ 
  uploadedOrgCharts,
  parsedOrgCharts,
  selectedUploadScope,
  onSetActiveView,
  onAnalyze,
  onDeleteUpload,
  onRenameUpload,
  onUploadComplete, // Callback after successful upload
  activeOrganization,
}) => {
  const [deletingId, setDeletingId] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const handleDelete = async (upload) => {
    if (!window.confirm(`Delete "${getDisplayTitle(upload)}"? This will remove all associated roles and suggestions.`)) {
      return;
    }
    
    setDeletingId(upload.scan_id);
    try {
      await onDeleteUpload(upload.scan_id);
    } finally {
      setDeletingId(null);
    }
  };

  // Initiate analysis - proceed directly without confirmation modal
  const handleAnalyzeClick = (upload) => {
    executeAnalysis(upload);
  };

  // Execute analysis after confirmation
  const executeAnalysis = async (upload) => {
    setAnalyzingId(upload.scan_id);
    try {
      await onAnalyze(upload.scan_id);
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleAnalyze = async (upload) => {
    setAnalyzingId(upload.scan_id);
    try {
      await onAnalyze(upload.scan_id);
    } finally {
      setAnalyzingId(null);
    }
  };

  // Start editing a title
  const handleStartEdit = (upload, e) => {
    e.stopPropagation();
    setEditingId(upload.scan_id);
    setEditingTitle(upload.custom_title || '');
  };

  // Save edited title
  const handleSaveTitle = async (upload) => {
    const trimmedTitle = editingTitle.trim();
    
    try {
      // Call backend to update title
      const response = await fetch(`${BACKEND_URL}/api/org/org-chart/uploads/${upload.scan_id}/title`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          title: trimmedTitle || null, // null clears custom title, reverts to date
          organization_id: activeOrganization?.organization_id
        })
      });
      
      if (response.ok) {
        // Update local state via callback if provided
        if (onRenameUpload) {
          onRenameUpload(upload.scan_id, trimmedTitle || null);
        }
      }
    } catch (error) {
      console.error('Error updating title:', error);
    }
    
    setEditingId(null);
    setEditingTitle('');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingTitle('');
  };

  // Get display title - user-defined takes priority over date-based
  const getDisplayTitle = (upload) => {
    if (upload.custom_title) {
      return upload.custom_title;
    }
    return `Org Chart — ${new Date(upload.uploaded_at).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    })}`;
  };

  // Check if an upload has been analyzed
  const isAnalyzed = (scanId) => {
    return parsedOrgCharts.some(parsed => parsed.scan_id === scanId);
  };

  // Get parsed data for an upload
  const getParsedData = (scanId) => {
    return parsedOrgCharts.find(parsed => parsed.scan_id === scanId);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('organization_id', activeOrganization?.organization_id);
      
      // Create a new scan_id for this upload
      const scanId = `scan_${Date.now()}`;
      formData.append('scan_id', scanId);
      
      const response = await fetch(`${BACKEND_URL}/api/org/org-chart/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Notify parent to refresh
        if (onUploadComplete) {
          onUploadComplete();
        }
      } else {
        const error = await response.json();
        console.error('Upload failed:', error);
        alert(typeof error.detail === 'string' ? error.detail : 'Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (uploadedOrgCharts.length === 0) {
    return (
      <div className="glass rounded-3xl p-12 text-center">
        <FileImage size={64} className="mx-auto mb-6 text-[#6366F1]" />
        <h2 className="text-3xl font-bold text-white mb-4">
          No Org Charts Yet
        </h2>
        <p className="text-xl text-[#A0AEC0] mb-4">
          UploadSimple your first org chart to get started with AI analysis.
        </p>
        <div className="flex items-center justify-center gap-2 px-3 py-2 mb-6 rounded-lg bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.2)] text-sm w-fit mx-auto" data-testid="credit-cost-estimate-org-chart-upload">
          <span className="text-[#A0AEC0]">Estimated AI analysis cost: <span className="text-white font-semibold">~75 credits</span></span>
        </div>
        <label className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white rounded-full font-semibold cursor-pointer hover:opacity-90 transition-opacity">
          <UploadSimple size={20} />
          UploadSimple Org Chart
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*,.pdf"
            multiple
            className="hidden"
          />
        </label>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* UploadSimple Button Header */}
      <div className="flex justify-end">
        <label className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white rounded-full font-semibold cursor-pointer hover:opacity-90 transition-opacity ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {isUploading ? (
            <>
              <ArrowsClockwise size={18} className="animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Plus size={18} />
              UploadSimple Org Chart
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*,.pdf"
            multiple
            className="hidden"
            disabled={isUploading}
          />
        </label>
      </div>

      {/* Org Charts Grid - Sorted by newest first */}
      <div className="grid md:grid-cols-2 gap-6">
        {[...uploadedOrgCharts]
          .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
          .map((upload) => {
          const isActive = selectedUploadScope === upload.scan_id;
          const isDeleting = deletingId === upload.scan_id;
          const isAnalyzing = analyzingId === upload.scan_id;
          const isEditing = editingId === upload.scan_id;
          const analyzed = isAnalyzed(upload.scan_id);
          const parsedData = getParsedData(upload.scan_id);
          
          // Get counts from parsed data if available
          const rolesCount = parsedData?.parsed_data?.roles?.length || parsedData?.roles_count || 0;
          const uniqueDepts = parsedData?.parsed_data?.roles
            ? [...new Set(parsedData.parsed_data.roles.map(r => r.department).filter(Boolean))]
            : parsedData?.departments || [];
          const deptCount = uniqueDepts.length || parsedData?.departments_count || 0;
          
          // Format date for display
          const uploadDate = new Date(upload.uploaded_at).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric'
          });
          
          return (
            <div 
              key={upload.scan_id} 
              className={`glass rounded-2xl p-6 space-y-4 transition-all ${
                isActive ? 'ring-2 ring-[#6366F1]' : ''
              }`}
            >
              {/* Org Chart Card Header - Editable title */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center flex-shrink-0">
                    <FileImage size={24} className="text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    {isEditing ? (
                      /* Inline Edit Mode */
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          placeholder={`Org Chart — ${uploadDate}`}
                          className="flex-1 px-3 py-1 bg-[rgba(255,255,255,0.1)] border border-[#6366F1] rounded-lg text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveTitle(upload);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                        />
                        <button
                          onClick={() => handleSaveTitle(upload)}
                          className="p-1.5 bg-[#10B981] rounded-lg text-white hover:bg-[#059669] transition-colors"
                          title="Save"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1.5 bg-[#EF4444] rounded-lg text-white hover:bg-[#DC2626] transition-colors"
                          title="Cancel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      /* Display Mode */
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          {getDisplayTitle(upload)}
                          {isActive && (
                            <span className="px-2 py-0.5 text-xs bg-[#6366F1] text-white rounded-full flex items-center gap-1">
                              <Check size={12} />
                              Active
                            </span>
                          )}
                        </h3>
                        <button
                          onClick={(e) => handleStartEdit(upload, e)}
                          className="p-1 text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.1)] rounded transition-colors"
                          title="Rename org chart"
                        >
                          <Pencil size={14} />
                        </button>
                      </div>
                    )}
                    <p className="text-sm text-[#A0AEC0]">
                      Uploaded {new Date(upload.uploaded_at).toLocaleTimeString('en-US', { 
                        hour: '2-digit',
                        minute: '2-digit'
                      })} on {uploadDate}
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              {analyzed && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="glass rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">{rolesCount}</p>
                    <p className="text-sm text-[#A0AEC0]">Roles Identified</p>
                  </div>
                  <div className="glass rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-white">{deptCount}</p>
                    <p className="text-sm text-[#A0AEC0]">Departments</p>
                  </div>
                </div>
              )}

              {/* Status and Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-[rgba(255,255,255,0.1)]">
                <div className="flex items-center gap-2">
                  {analyzed ? (
                    <>
                      <Sparkle size={18} className="text-[#10B981]" />
                      <span className="text-sm text-[#10B981] font-semibold">AI Analyzed</span>
                    </>
                  ) : (
                    <>
                      <Sparkle size={18} className="text-[#A0AEC0]" />
                      <span className="text-sm text-[#A0AEC0]">Pending Analysis</span>
                    </>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Set as Active */}
                  {!isActive && (
                    <button
                      onClick={() => onSetActiveView(upload.scan_id)}
                      className="px-3 py-1.5 text-sm glass hover:bg-[rgba(255,255,255,0.1)] text-white rounded-lg transition-all font-semibold"
                    >
                      Set as Active
                    </button>
                  )}
                  
                  {/* Re-analyze */}
                  {analyzed && (
                    <button
                      onClick={() => handleAnalyzeClick(upload)}
                      disabled={isAnalyzing}
                      className="p-2 glass hover:bg-[rgba(255,255,255,0.1)] text-[#A0AEC0] hover:text-white rounded-lg transition-all disabled:opacity-50"
                      title="Re-analyze"
                    >
                      <ArrowsClockwise size={16} className={isAnalyzing ? 'animate-spin' : ''} />
                    </button>
                  )}
                  
                  {/* Analyze (if not analyzed) */}
                  {!analyzed && (
                    <button
                      onClick={() => handleAnalyzeClick(upload)}
                      disabled={isAnalyzing}
                      className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                    >
                      {isAnalyzing ? (
                        <>
                          <ArrowsClockwise size={16} className="animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkle size={16} />
                          Analyze
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Trash */}
                  <button
                    onClick={() => handleDelete(upload)}
                    disabled={isDeleting}
                    className="p-2 glass hover:bg-[rgba(239,68,68,0.2)] text-[#A0AEC0] hover:text-[#EF4444] rounded-lg transition-all disabled:opacity-50"
                    title="Delete"
                  >
                    <Trash size={16} className={isDeleting ? 'animate-pulse' : ''} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Analysis Confirmation Modal */}
    </div>
  );
};

export default UploadsView;
